import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatEUR } from "@/lib/api";
import { toast } from "sonner";
import MandatsASecuriser from "./MandatsASecuriser";
import RappelsDemandes from "./RappelsDemandes";
import BosControles from "./BosControles";
import DossiersEnRetard from "./DossiersEnRetard";
import EmailRelanceButton from "./EmailRelanceButton";
import BriefingStrategiqueButton from "./BriefingStrategiqueButton";
import { track, USAGE_EVENTS } from "@/lib/usage";
import {
  Target,
  Fire,
  Lightning,
  Warning,
  Phone,
  ArrowRight,
  CheckCircle,
  TrendUp,
  Heart,
  ClockCounterClockwise,
  Sparkle,
  Spinner,
} from "@phosphor-icons/react";

// V4.35 — Mapping kind → audience pour le bouton ✉️ Email.
// Permet de proposer les bons templates selon le type de relance.
function audienceForKind(kind) {
  if (kind === "accord_sans_suivi") return "banque";
  // Tout le reste est une relance client (relance_retard, sans_action, offre_non_signee, etc.)
  return "client";
}

/**
 * V4.20 — Commando Today.
 *
 * Doctrine : "Que dois-je faire MAINTENANT pour générer du chiffre ?"
 *
 * 5 blocs scannables mobile-first (consultation plusieurs fois/jour) :
 *   1. Hero Objectif du jour (4 compteurs visibles en < 2s)
 *   2. Priorité unique (bandeau dominant, depuis Pilotage)
 *   3. Relances critiques (multi-source, action sur place)
 *   4. Dossiers chauds (CA potentiel + maturité + dernière action)
 *   5. Santé pipeline (5 compteurs compacts)
 *
 * Un seul appel API → /api/commando/today
 */

const STAGE_LABELS = {
  prospect: "Prospect",
  compromis: "Compromis",
  envoi_banque: "En banque",
  accord_banque: "Accord banque",
  accord_risque: "Accord risque",
  rdv_bancaire: "RDV bancaire",
  offre_editee: "Offre éditée",
  acte_authentique: "Acte authentique",
};

const KIND_META = {
  relance_retard: { icon: Warning, color: "var(--cf-danger)", bg: "var(--cf-danger-soft)", label: "Retard" },
  offre_non_signee: { icon: Fire, color: "var(--cf-warning)", bg: "var(--cf-warning-soft)", label: "Closing" },
  accord_sans_suivi: { icon: ClockCounterClockwise, color: "var(--cf-accent)", bg: "var(--cf-accent-soft)", label: "Suivi" },
  sans_action: { icon: Sparkle, color: "var(--cf-info)", bg: "var(--cf-primary-soft)", label: "Réveil" },
};

const SEV_META = {
  critique: { bg: "linear-gradient(135deg,var(--cf-danger-soft) 0%,var(--cf-warning-soft) 100%)", fg: "var(--cf-danger-text)", accent: "var(--cf-danger)", overline: "var(--cf-danger-text)" },
  alerte: { bg: "linear-gradient(135deg,var(--cf-warning-soft) 0%,var(--cf-warning-soft) 100%)", fg: "var(--cf-warning-text)", accent: "var(--cf-warning)", overline: "var(--cf-warning-text)" },
  ok: { bg: "linear-gradient(135deg,var(--cf-success-soft) 0%,var(--cf-success-soft) 100%)", fg: "var(--cf-success-text)", accent: "var(--cf-success)", overline: "var(--cf-success-text)" },
};

// ============================================================================
// BLOC 1 — Hero Objectif du jour
// ============================================================================
function ObjectifDuJour({ data, today, treatedRelancesCount = 0, signedMandatsCount = 0 }) {
  const o = data;
  const todayLabel = new Date(today).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });

  // V4.21 — Compteur "relances" affiché en LIVE (décrémente à chaque action)
  const relancesLive = Math.max(0, (o.relances_prioritaires || 0) - treatedRelancesCount);
  // V4.22 — Compteur "mandats" affiché en LIVE (décrémente à chaque mandat signé depuis Commando)
  const mandatsLive = Math.max(0, (o.mandats_a_signer || 0) - signedMandatsCount);

  // Une "carte" par compteur — gros chiffre, label court
  const cells = [
    { value: o.contacts_a_creer, label: "contacts", color: "var(--cf-primary-hover)", testid: "obj-contacts" },
    { value: mandatsLive, label: "mandats", color: "var(--cf-success-text)", testid: "obj-mandats" },
    { value: relancesLive, label: "relances", color: "var(--cf-warning-text)", testid: "obj-relances" },
    { value: o.dossiers_chauds, label: "chauds", color: "var(--cf-danger-text)", testid: "obj-chauds" },
  ];

  return (
    <section
      className="rounded-3xl p-5 md:p-6 mb-4"
      style={{ background: "linear-gradient(135deg,var(--cf-text) 0%,var(--cf-text) 100%)" }}
      data-testid="commando-objectif-du-jour"
    >
      <div className="flex items-center gap-2 mb-1">
        <Target size={16} weight="fill" color="var(--cf-warning)" />
        <div className="overline" style={{ color: "var(--cf-warning)" }}>
          Objectif du jour
        </div>
        {/* V4.42 — Briefing stratégique exportable depuis Commando */}
        <div className="ml-auto">
          <BriefingStrategiqueButton variant="ghost" testid="briefing-copy-btn-commando" />
        </div>
      </div>
      <p className="text-[12px] text-slate-300 font-semibold capitalize">{todayLabel}</p>

      <div className="grid grid-cols-4 gap-2 md:gap-3 mt-4">
        {cells.map((c) => (
          <div key={c.label} className="text-center" data-testid={c.testid}>
            <div
              className="font-display text-3xl md:text-5xl font-extrabold tabular-nums leading-none"
              style={{ color: "#FFFFFF" }}
            >
              {c.value}
            </div>
            <div className="text-[10px] md:text-[11px] font-bold uppercase tracking-wider mt-1" style={{ color: c.color, opacity: 0.95 }}>
              {c.label}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between text-[11px] font-bold text-slate-400">
        <span>
          <span className="text-white tabular-nums">{o.contacts_realises_mois}</span> contacts ·
          <span className="text-white tabular-nums ml-1">{o.mandats_realises_mois}/{o.mandats_objectif_mois}</span> mandats
        </span>
        <span>
          <span className="text-white tabular-nums">{o.jours_restants}j</span> restants
        </span>
      </div>
    </section>
  );
}

// ============================================================================
// BLOC 2 — Priorité unique
// ============================================================================
function PrioriteUnique({ data }) {
  const meta = SEV_META[data.severity] || SEV_META.ok;
  // V4.34 — Indicateur de fiabilité discret (3 dots) basé sur sample_size + jours ouvrés écoulés.
  // Reste optionnel : si le backend n'expose pas la fiabilité, on ne montre rien.
  const fia = data.fiabilite;
  const fiaLabel = { elevee: "Fiabilité élevée", moyenne: "Fiabilité moyenne", faible: "Fiabilité faible" }[fia];
  const fiaColor = fia === "elevee" ? "var(--cf-success)" : fia === "moyenne" ? "var(--cf-warning)" : "var(--cf-text-3)";
  const fiaDots = fia === "elevee" ? 3 : fia === "moyenne" ? 2 : 1;
  return (
    <section
      className="rounded-2xl p-4 md:p-5 mb-4"
      style={{ background: meta.bg }}
      data-testid="commando-priorite-unique"
    >
      <div className="flex items-center gap-2 mb-2">
        <Lightning size={14} weight="fill" color={meta.accent} />
        <div className="overline" style={{ color: meta.overline }}>
          Priorité unique
        </div>
        {fia && (
          <div
            className="ml-auto flex items-center gap-1"
            title={`${fiaLabel} — ${data.sample_size} mandat(s) ce mois · ${data.jours_ouvres_ecoules}/${data.jours_ouvres_total} jours ouvrés`}
            data-testid={`commando-fiabilite-${fia}`}
          >
            <span className="flex items-center gap-0.5">
              {[1, 2, 3].map((i) => (
                <span
                  key={i}
                  className="w-1 h-1 rounded-full"
                  style={{ background: i <= fiaDots ? fiaColor : "rgba(255,255,255,0.35)" }}
                />
              ))}
            </span>
          </div>
        )}
      </div>
      <p
        className="font-display text-lg md:text-2xl font-extrabold leading-tight tracking-tight"
        style={{ color: meta.fg }}
        data-testid="commando-priorite-label"
      >
        {data.label}
      </p>
      <p
        className="font-display text-sm md:text-base font-bold mt-1.5 leading-snug"
        style={{ color: meta.fg, opacity: 0.85 }}
        data-testid="commando-priorite-action"
      >
        {data.action}
      </p>
    </section>
  );
}

// ============================================================================
// BLOC 3 — Relances critiques
// ============================================================================
function RelancesCritiques({ items, onMarkRelance, markingId, treatedIds }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  if (!items?.length) return null;
  const display = expanded ? items : items.slice(0, 5);

  return (
    <section className="card-base p-4 mb-4" data-testid="commando-relances-critiques">
      <div className="flex items-center gap-2 mb-3">
        <Warning size={16} weight="fill" color="var(--cf-danger)" />
        <h2 className="font-display text-base md:text-lg font-extrabold text-slate-900">
          Relances critiques
        </h2>
        <span className="ml-auto px-2 py-0.5 rounded-full bg-[color:var(--cf-danger-soft)] text-[color:var(--cf-danger-text)] text-[11px] font-extrabold tabular-nums">
          {items.length - treatedIds.size}
        </span>
      </div>

      <ul className="space-y-2">
        {display.map((it) => {
          const km = KIND_META[it.kind] || KIND_META.sans_action;
          const isTreated = treatedIds.has(it.id);
          return (
            <li
              key={it.id}
              onClick={() => !isTreated && navigate(`/dossier/${it.id}`)}
              className={`rounded-xl p-3 border border-slate-100 transition-all duration-200 flex items-center gap-3 ${
                isTreated
                  ? "opacity-50 bg-slate-50 cursor-default"
                  : "cursor-pointer hover:bg-slate-50"
              }`}
              data-testid={`commando-relance-${it.id}`}
              data-treated={isTreated ? "true" : "false"}
            >
              {/* Icône kind */}
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: km.bg, color: km.color }}
                title={km.label}
              >
                <km.icon size={15} weight="fill" />
              </div>

              {/* Nom + raison */}
              <div className="flex-1 min-w-0">
                <div
                  className={`font-bold text-[13.5px] truncate ${
                    isTreated ? "text-slate-500 line-through decoration-1" : "text-slate-900"
                  }`}
                >
                  {it.nom || it.client_name}
                </div>
                <div className="text-[11px] text-slate-500 font-semibold leading-tight">
                  {it.reason} · {STAGE_LABELS[it.etape] || it.etape}
                </div>
              </div>

              {/* CA + action / état traité */}
              <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                {it.ca_net > 0 && (
                  <span
                    className={`text-[11px] font-extrabold tabular-nums ${
                      isTreated ? "text-slate-400" : "text-slate-700"
                    }`}
                  >
                    {formatEUR(it.ca_net)}
                  </span>
                )}
                {isTreated ? (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-100 text-slate-500 text-[10.5px] font-extrabold cursor-default"
                    data-testid={`commando-relance-treated-${it.id}`}
                  >
                    <CheckCircle size={11} weight="fill" /> Traité
                  </span>
                ) : (
                  <div
                    className="inline-flex items-center gap-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* V4.35 — Bouton ✉️ Email avec dropdown de templates compatibles.
                        N'auto-marque PAS la relance comme traitée (doctrine : fiabilité > vitesse). */}
                    <EmailRelanceButton
                      audience={audienceForKind(it.kind)}
                      dossier={it}
                      compact
                      testid={`commando-relance-email-${it.id}`}
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onMarkRelance(it);
                      }}
                      disabled={markingId === it.id}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-[color:var(--cf-success-soft)] text-[color:var(--cf-success-text)] text-[10.5px] font-extrabold hover:brightness-95 disabled:opacity-60 transition-all"
                      data-testid={`commando-relance-mark-${it.id}`}
                    >
                      {markingId === it.id ? (
                        <Spinner size={10} weight="bold" className="animate-spin" />
                      ) : (
                        <Phone size={10} weight="fill" />
                      )}
                      <span>Relancé</span>
                    </button>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {items.length > 5 && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="mt-2 text-[11.5px] font-bold text-slate-500 hover:text-slate-700"
          data-testid="commando-relances-toggle"
        >
          {expanded ? "Réduire" : `Voir les ${items.length - 5} autres`}
        </button>
      )}
    </section>
  );
}

// ============================================================================
// BLOC 4 — Dossiers chauds
// ============================================================================
function DossiersChauds({ items, onMarkMandatSigned, signingId, signedIds }) {
  const navigate = useNavigate();
  if (!items?.length) return null;
  const visibleCount = items.length - signedIds.size;
  return (
    <section className="card-base p-4 mb-4" data-testid="commando-dossiers-chauds">
      <div className="flex items-center gap-2 mb-3">
        <Fire size={16} weight="fill" color="var(--cf-warning)" />
        <h2 className="font-display text-base md:text-lg font-extrabold text-slate-900">
          Dossiers chauds
        </h2>
        <span className="ml-auto px-2 py-0.5 rounded-full bg-[color:var(--cf-warning-soft)] text-[color:var(--cf-warning-text)] text-[11px] font-extrabold tabular-nums">
          {visibleCount}
        </span>
      </div>

      <ul className="space-y-2">
        {items.map((c) => {
          const mt = c.maturity;
          const matColor = mt >= 85 ? "var(--cf-success)" : mt >= 65 ? "var(--cf-warning)" : "var(--cf-info)";
          const isSigned = signedIds.has(c.id);
          const canSignMandat = c.axis === "mandat" && !isSigned;
          return (
            <li
              key={c.id}
              onClick={() => !isSigned && navigate(`/dossier/${c.id}`)}
              className={`rounded-xl p-3 border border-slate-100 transition-all duration-200 ${
                isSigned
                  ? "opacity-50 bg-slate-50 cursor-default"
                  : "cursor-pointer hover:bg-slate-50"
              }`}
              data-testid={`commando-chaud-${c.id}`}
              data-signed={isSigned ? "true" : "false"}
            >
              <div className="flex items-center gap-3">
                {/* Pastille maturité */}
                <div
                  className="w-11 h-11 rounded-xl flex flex-col items-center justify-center flex-shrink-0 text-white"
                  style={{ background: matColor }}
                >
                  <span className="font-display text-[15px] font-extrabold tabular-nums leading-none">{mt}</span>
                  <span className="text-[8px] font-bold uppercase tracking-wide opacity-80 mt-0.5">mat.</span>
                </div>
                {/* Nom + angle */}
                <div className="flex-1 min-w-0">
                  <div
                    className={`font-bold text-[13.5px] truncate ${
                      isSigned ? "text-slate-500 line-through decoration-1" : "text-slate-900"
                    }`}
                  >
                    {c.nom || c.client_name}
                  </div>
                  <div className="text-[11px] font-semibold text-slate-500 leading-tight">
                    {c.axis_label} · {STAGE_LABELS[c.etape] || c.etape}
                  </div>
                </div>
                {/* CA + dernière action */}
                <div className="text-right flex-shrink-0">
                  <div
                    className={`font-display text-[13.5px] font-extrabold tabular-nums ${
                      isSigned ? "text-slate-400" : "text-slate-900"
                    }`}
                  >
                    {formatEUR(c.ca_potentiel)}
                  </div>
                  <div className="text-[10.5px] font-semibold text-slate-400">
                    {c.days_since_last_action != null
                      ? `il y a ${c.days_since_last_action}j`
                      : "—"}
                  </div>
                </div>
                {/* Action ou flèche */}
                {isSigned ? (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-100 text-slate-500 text-[10.5px] font-extrabold cursor-default flex-shrink-0"
                    data-testid={`commando-chaud-signed-${c.id}`}
                  >
                    <CheckCircle size={11} weight="fill" /> Signé
                  </span>
                ) : canSignMandat ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMarkMandatSigned(c);
                    }}
                    disabled={signingId === c.id}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-[color:var(--cf-success-soft)] text-[color:var(--cf-success-text)] text-[10.5px] font-extrabold hover:brightness-95 disabled:opacity-60 transition-all flex-shrink-0"
                    data-testid={`commando-chaud-sign-${c.id}`}
                    title="Marquer le mandat comme signé (aujourd'hui)"
                  >
                    {signingId === c.id ? (
                      <Spinner size={10} weight="bold" className="animate-spin" />
                    ) : (
                      <CheckCircle size={10} weight="fill" />
                    )}
                    <span>Mandat signé</span>
                  </button>
                ) : (
                  <ArrowRight size={14} className="text-slate-300 flex-shrink-0" />
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ============================================================================
// BLOC 5 — Santé pipeline (compact)
// ============================================================================
function SantePipeline({ data }) {
  const items = [
    { label: "En attente", value: data.en_attente, color: "var(--cf-text-3)" },
    { label: "Mandats", value: data.mandats, color: "var(--cf-info)" },
    { label: "En banque", value: data.en_banque, color: "var(--cf-primary-hover)" },
    { label: "Accords", value: data.accords, color: "var(--cf-accent)" },
    { label: "Signés mois", value: data.signes_mois, color: "var(--cf-success)" },
  ];
  return (
    <section className="card-base p-4 mb-4" data-testid="commando-pipeline-sante">
      <div className="flex items-center gap-2 mb-3">
        <Heart size={14} weight="fill" color="var(--cf-info)" />
        <h2 className="font-display text-[14px] md:text-base font-extrabold text-slate-900">
          Santé pipeline
        </h2>
      </div>
      <div className="grid grid-cols-5 gap-2">
        {items.map((it) => (
          <div key={it.label} className="text-center" data-testid={`pipeline-${it.label.toLowerCase().replace(' ', '-')}`}>
            <div
              className="font-display text-2xl md:text-3xl font-extrabold tabular-nums leading-none"
              style={{ color: it.color }}
            >
              {it.value}
            </div>
            <div className="text-[9.5px] md:text-[10px] font-bold uppercase tracking-wider text-slate-500 mt-1 leading-tight">
              {it.label}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ============================================================================
// Composant principal
// ============================================================================
export default function CommandoToday({ refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [markingId, setMarkingId] = useState(null);
  // V4.21 — Set des dossiers déjà traités côté UI (relances)
  const [treatedIds, setTreatedIds] = useState(() => new Set());
  // V4.22 — Set des dossiers dont le mandat vient d'être signé (optimiste)
  const [signedIds, setSignedIds] = useState(() => new Set());
  const [signingId, setSigningId] = useState(null);

  const fetchToday = useCallback(async () => {
    try {
      const r = await api.get("/commando/today");
      setData(r.data);
      // Reset : les items vraiment traités ont disparu côté backend.
      setTreatedIds(new Set());
      setSignedIds(new Set());
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchToday();
    const onVisibility = () => {
      if (document.visibilityState === "visible") fetchToday();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onVisibility);
    };
  }, [fetchToday, refreshKey]);

  const handleMarkRelance = async (item) => {
    if (treatedIds.has(item.id)) return;
    setMarkingId(item.id);
    setTreatedIds((prev) => {
      const next = new Set(prev);
      next.add(item.id);
      return next;
    });
    // V4.36 — Track avec days_overdue pour calculer le temps moyen de traitement
    track(USAGE_EVENTS.COMMANDO_RELANCE, {
      dossier_id: item.id,
      etape: item.etape,
      meta: { kind: item.kind, days_overdue: item.days_overdue || 0 },
    });
    try {
      await api.post(`/dossiers/${item.id}/relance`);
    } catch {
      setTreatedIds((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
      toast.error("Impossible de noter la relance");
    } finally {
      setMarkingId(null);
    }
  };

  // V4.22 — Mandat signé optimiste (toggle endpoint qui préserve la date V4.18)
  const handleMarkMandatSigned = async (item) => {
    if (signedIds.has(item.id)) return;
    setSigningId(item.id);
    setSignedIds((prev) => {
      const next = new Set(prev);
      next.add(item.id);
      return next;
    });
    // V4.36 — Track mandat signé depuis Commando
    track(USAGE_EVENTS.COMMANDO_MANDAT_SIGNE, { dossier_id: item.id, etape: item.etape });
    try {
      // POST /api/dossiers/{id}/mandat toggle :
      //   - si mandat_signe=false → passe à true. Le backend stamp today UNIQUEMENT
      //     si mandat_signe_date est absente (logique préservation V4.18).
      //   - Tous les items "axis=mandat" ont mandat_signe=false (sinon ils ne seraient
      //     pas dans near_mandat), donc l'appel les bascule à true en toute sécurité.
      await api.post(`/dossiers/${item.id}/mandat`);
    } catch {
      setSignedIds((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
      toast.error("Impossible de marquer le mandat signé");
    } finally {
      setSigningId(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3" data-testid="commando-today-loading">
        <div className="h-44 bg-slate-100 rounded-3xl animate-pulse" />
        <div className="h-24 bg-slate-100 rounded-2xl animate-pulse" />
        <div className="h-32 bg-slate-100 rounded-2xl animate-pulse" />
      </div>
    );
  }
  if (!data) return null;

  const treatedInCurrentList = (data.relances_critiques || []).filter((r) => treatedIds.has(r.id)).length;
  // Combien de mandats signés depuis Commando appartiennent au compteur "mandats_a_signer" ?
  // Tous les items axis="mandat" comptent (ils étaient dans le funnel à signer)
  const signedInCurrentList = (data.dossiers_chauds || []).filter(
    (c) => c.axis === "mandat" && signedIds.has(c.id)
  ).length;

  return (
    <div data-testid="commando-today-root">
      <RappelsDemandes items={data.rappels_demandes || []} />
      <ObjectifDuJour
        data={data.objectif_du_jour}
        today={data.today}
        treatedRelancesCount={treatedInCurrentList}
        signedMandatsCount={signedInCurrentList}
      />
      <PrioriteUnique data={data.priorite_unique} />
      <MandatsASecuriser
        mandats={data.mandats_a_securiser || []}
        dormants={data.dormants_a_reactiver || []}
      />
      <BosControles items={data.bos_a_controler || []} />
      {/* V4.38 — Dossiers en retard (étape > délai cible) — Safe = Silence si vide */}
      <DossiersEnRetard items={data.dossiers_en_retard || []} />
      <RelancesCritiques
        items={data.relances_critiques}
        onMarkRelance={handleMarkRelance}
        markingId={markingId}
        treatedIds={treatedIds}
      />
      <DossiersChauds
        items={data.dossiers_chauds}
        onMarkMandatSigned={handleMarkMandatSigned}
        signingId={signingId}
        signedIds={signedIds}
      />
      <SantePipeline data={data.pipeline_sante} />
    </div>
  );
}
